import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Selection } from './orgNavTypes';
import type { FilterInput } from './orgNavTypes';
import type { SelectionState } from './useOrgNavState';

/**
 * Single source of truth for ALL OrgNav URL state — tab, organization filters,
 * and the type/id detail selection — stored in the URL so browser back/forward
 * restores it. One useSearchParams instance owns every write: two separate
 * setSearchParams calls in a handler read the same stale params and the last
 * clobbers the first, so selection lives here too rather than in a sibling hook.
 */

const FILTER_KEYS = ['companies', 'resellers', 'locations'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

const toMap = (csv: string | null): Record<string, boolean> =>
    (csv ? csv.split(',').filter(Boolean) : []).reduce<Record<string, boolean>>(
        (selected, id) => ({ ...selected, [id]: true }),
        {},
    );

const toCsv = (map: Record<string, boolean>): string =>
    Object.keys(map)
        .filter((id) => map[id])
        .join(',');

export const useOrgFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedTab = (searchParams.get('tab') as Selection) || Selection.Company;

    const orgFilters = useMemo<FilterInput>(
        () => ({
            companies: toMap(searchParams.get('companies')),
            resellers: toMap(searchParams.get('resellers')),
            locations: toMap(searchParams.get('locations')),
        }),
        [searchParams],
    );

    const write = (filters: FilterInput, tab?: Selection) =>
        setSearchParams((prev) => {
            const merged = new URLSearchParams(prev);
            FILTER_KEYS.forEach((key) => {
                const csv = toCsv(filters[key]);
                if (csv) merged.set(key, csv);
                else merged.delete(key);
            });
            if (tab) merged.set('tab', tab);
            return merged;
        });

    // Switching tabs also closes any open detail (type/id). Both happen in one
    // write — two separate setSearchParams calls in a handler read the same
    // stale params and the last one clobbers the first.
    const setSelectedTab = (tab: Selection) =>
        setSearchParams((prev) => {
            const merged = new URLSearchParams(prev);
            merged.set('tab', tab);
            merged.delete('type');
            merged.delete('id');
            return merged;
        });

    const setOrgFilter = (key: FilterKey, value: string, selectOne?: boolean) => {
        const isActive = orgFilters[key][value];
        const next: FilterInput = {
            ...orgFilters,
            [key]: { ...(selectOne ? {} : orgFilters[key]), [value]: !isActive },
        };

        // Removing a parent cascades to its children, matching the prior behavior.
        if (isActive) {
            if (key === 'companies') {
                next.resellers = {};
                next.locations = {};
            } else if (key === 'resellers') {
                next.locations = {};
            }
        }

        write(next);
    };

    const setFilters = (filters: FilterInput, tab: Selection) => write(filters, tab);

    const clearOrgFilters = () => write({ companies: {}, resellers: {}, locations: {} });

    // type/id detail selection — same instance as tab/filters so opening and
    // closing a detail preserves them in a single, non-clobbering write.
    const selection = useMemo<SelectionState>(() => {
        const type = searchParams.get('type');
        const id = searchParams.get('id');
        return {
            type: (type as Selection) || null,
            id: id ? (Number.isNaN(Number(id)) ? id : parseInt(id, 10)) : null,
        };
    }, [searchParams]);

    const openDetail = (type: Selection, id: string | number | null) =>
        setSearchParams((prev) => {
            const merged = new URLSearchParams(prev);
            merged.set('type', type);
            if (id !== null && id !== undefined && id !== '') merged.set('id', String(id));
            else merged.delete('id');
            return merged;
        });

    const clearSelection = () =>
        setSearchParams((prev) => {
            const merged = new URLSearchParams(prev);
            merged.delete('type');
            merged.delete('id');
            return merged;
        });

    return {
        selectedTab,
        setSelectedTab,
        orgFilters,
        setOrgFilter,
        setFilters,
        clearOrgFilters,
        selection,
        openDetail,
        clearSelection,
    };
};
